/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react'
import { Modal, Button, Form, Checkbox, List } from 'semantic-ui-react'
import { TemplateService } from 'utility'
import { FlexRowBetween, TitleText, LoaderBar } from 'components'
import { X } from 'phosphor-react'
import { Images } from 'assets'
import packageJson from '../../package.json'

export const CommonPopUpModal = ({ open, onClose, children, title }) => {
    return (
        <Modal
            title={title}
            open={open}
            onCancel={() => onClose()}
            className="model"
            size="small"
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>{title}</TitleText>
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>{children}</Modal.Content>
        </Modal>
    )
}

// Reusable component for AutoEmail + template selection
export const TemplateAutoEmailSelector = ({
    autoEmail,
    setAutoEmail,
    selectedTemplates,
    setSelectedTemplates,
    disabled,
}) => {
    const [templates, setTemplates] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectAll, setSelectAll] = useState(false)
    const [error, setError] = useState('')

    // Fetch templates when autoEmail is checked
    useEffect(() => {
        if (autoEmail) {
            fetchTemplates()
        }
    }, [autoEmail])

    // Update selectAll state when selectedTemplates changes
    useEffect(() => {
        if (templates.length > 0) {
            setSelectAll(selectedTemplates.length === templates.length)
        }
    }, [selectedTemplates, templates])

    const fetchTemplates = async () => {
        try {
            setLoading(true)
            const response = await TemplateService.templateList({
                PageNumber: 1,
                PageSize: 100,
            })
            let templateData = []
            if (response.Records) {
                templateData = response.Records
            } else if (response.Items) {
                templateData = response.Items
            } else if (Array.isArray(response)) {
                templateData = response
            }
            setTemplates(templateData)
        } catch (error) {
            setError('Failed to load templates:')
        } finally {
            setLoading(false)
        }
    }

    const handleTemplateToggle = templateId => {
        if (selectedTemplates.includes(templateId)) {
            setSelectedTemplates(
                selectedTemplates.filter(id => id !== templateId),
            )
        } else {
            setSelectedTemplates([...selectedTemplates, templateId])
        }
    }

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedTemplates([])
        } else {
            setSelectedTemplates(templates.map(template => template.Id))
        }
        setSelectAll(!selectAll)
    }

    return (
        <>
            <Form.Field>
                <Checkbox
                    label="AutoEmail"
                    checked={autoEmail}
                    onChange={() => setAutoEmail(!autoEmail)}
                    disabled={disabled}
                />
            </Form.Field>
            {autoEmail && (
                <>
                    <h4>Select Templates for Email Notifications</h4>
                    {loading ? (
                        <LoaderBar content="Loading templates..." />
                    ) : error ? (
                        <div className="ui negative message">
                            <p>{error}</p>
                        </div>
                    ) : templates.length === 0 ? (
                        <div className="ui warning message">
                            <p>No templates available to select.</p>
                        </div>
                    ) : (
                        <>
                            <Form.Field>
                                <Checkbox
                                    label="Select All Templates"
                                    checked={selectAll}
                                    onChange={handleSelectAll}
                                />
                            </Form.Field>
                            <div
                                style={{
                                    maxHeight: '250px',
                                    overflowY: 'auto',
                                    marginTop: '10px',
                                    border: '1px solid #ddd',
                                    padding: '10px',
                                    borderRadius: '4px',
                                }}
                            >
                                <List divided relaxed>
                                    {templates.map(template => (
                                        <List.Item key={template.Id}>
                                            <List.Content>
                                                <Checkbox
                                                    label={
                                                        template.TemplateName ||
                                                        `Template ${template.Id}`
                                                    }
                                                    checked={selectedTemplates.includes(
                                                        template.Id,
                                                    )}
                                                    onChange={() =>
                                                        handleTemplateToggle(
                                                            template.Id,
                                                        )
                                                    }
                                                />
                                            </List.Content>
                                        </List.Item>
                                    ))}
                                </List>
                            </div>
                            <div style={{ marginTop: '10px' }}>
                                Selected templates: {selectedTemplates.length}
                            </div>
                        </>
                    )}
                </>
            )}
        </>
    )
}

export const GroupModal = ({
    isOpen,
    onClose,
    onSubmit,
    initialValues = {},
}) => {
    const [groupName, setGroupName] = useState(
        initialValues.TeamGroupName || initialValues.GroupName || '',
    )
    const [isCapa, setIsCapa] = useState(initialValues.IsCapa || false)
    const [autoEmail, setAutoEmail] = useState(initialValues.AutoEmail || false)
    const [selectedTemplates, setSelectedTemplates] = useState(
        initialValues.TemplateId || [],
    )

    // Reset form when initialValues or isOpen changes
    useEffect(() => {
        if (isOpen) {
            setGroupName(
                initialValues.TeamGroupName || initialValues.GroupName || '',
            )
            setIsCapa(initialValues.IsCapa || false)
            setAutoEmail(initialValues.AutoEmail || false)
            setSelectedTemplates(initialValues.TemplateId || [])
        } else {
            // Reset form when modal is closed
            resetForm()
        }
    }, [isOpen, initialValues])

    // Function to reset the form
    const resetForm = () => {
        setGroupName('')
        setIsCapa(false)
        setAutoEmail(false)
        setSelectedTemplates([])
    }

    const handleSubmit = () => {
        // Create payload with proper format
        const payload = {
            TeamGroupName: groupName,
            IsCapa: isCapa,
            AutoEmail: autoEmail,
            TemplateId: autoEmail ? selectedTemplates : null,
        }
        onSubmit(payload)
        resetForm()
    }

    const handleClose = () => {
        resetForm()
        onClose()
    }

    return (
        <Modal open={isOpen} onClose={handleClose} size="small">
            <Modal.Header>
                <FlexRowBetween>
                    {initialValues.TeamGroupId ? 'Edit Notification Group' : 'Create Notification Group'}
                    <X
                        className="pointer"
                        size={32}
                        onClick={() => onClose()}
                    />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content style={{ maxHeight: '70vh', overflow: 'auto' }}>
                <Form>
                    <Form.Field>
                        <label>Group Name</label>
                        <input
                            placeholder="Enter group name"
                            value={groupName}
                            onChange={e => setGroupName(e.target.value)}
                        />
                    </Form.Field>
                    {/* <Form.Field>
                        <Checkbox
                            label="IsCapa"
                            checked={isCapa}
                            onChange={() => setIsCapa(!isCapa)}
                        />
                    </Form.Field> */}
                    <TemplateAutoEmailSelector
                        autoEmail={autoEmail}
                        setAutoEmail={setAutoEmail}
                        selectedTemplates={selectedTemplates}
                        setSelectedTemplates={setSelectedTemplates}
                    />
                </Form>
            </Modal.Content>
            <Modal.Actions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    primary
                    onClick={handleSubmit}
                    disabled={
                        !groupName ||
                        (autoEmail && selectedTemplates.length === 0)
                    }
                >
                    Submit
                </Button>
            </Modal.Actions>
        </Modal>
    )
}

export const AboutModal = ({ open, onClose }) => {
    return (
        <Modal open={open} onCancel={onClose} className="model" size="small">
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>About Us</TitleText>
                    <X className="pointer" size={32} onClick={onClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <div style={{ textAlign: 'center', padding: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 16 }}>
                        <img
                            src={Images.Logo}
                            alt="EAuditor Logo"
                            style={{ width: 80, height: 80, background: '#1a3d59', border: '1px solid #ddd', borderRadius: 8, padding: 8 }}
                        />
                    </div>
                    <h2 style={{ marginBottom: 4 }}>EAuditor</h2>
                    <div style={{ color: '#888', fontSize: 14, marginBottom: 16 }}>
                        Version: {packageJson.version}
                    </div>
                    <div style={{ margin: '0 auto', maxWidth: 500, textAlign: 'left' }}>
                        <h3 style={{ color: '#1a3d59', marginBottom: 4 }}>About Us</h3>
                        <div style={{ height: 3, width: 40, background: '#6c63ff', marginBottom: 12 }} />
                        <p style={{ color: '#222', fontSize: 15, marginBottom: 20 }}>
                            eAuditor provides a comprehensive solution for managing compliance, risk, quality. eAuditor software streamlines audit-related tasks which include creating standardized audit and checklist templates, generating an audit plan, conducting audits.
                        </p>
                        <h3 style={{ color: '#1a3d59', marginBottom: 4 }}>Contact Info</h3>
                        <div style={{ height: 3, width: 40, background: '#6c63ff', marginBottom: 12 }} />
                        <div style={{ color: '#222', fontSize: 15, lineHeight: 1.7 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span role="img" aria-label="phone">📞</span> 866-351-4287
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span role="img" aria-label="phone">📞</span> 919-771-1510
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span role="img" aria-label="email">✉️</span> info@eauditor.app
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span role="img" aria-label="website">🌐</span> <a href="https://lyonsinfo.com/" target="_blank" rel="noopener noreferrer" style={{ color: '#1e3fd4', textDecoration: 'underline' }}>https://lyonsinfo.com/</a>
                            </div>
                        </div>
                        <h3 style={{ color: '#1a3d59', marginBottom: 4, marginTop: 24 }}>Schedule a Meeting</h3>
                        <div style={{ height: 3, width: 40, background: '#6c63ff', marginBottom: 12 }} />
                        <div style={{ color: '#222', fontSize: 15, lineHeight: 1.7 }}>
                            <p style={{ marginBottom: 12 }}>
                                Schedule a time to review your questions and requirements:
                            </p>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span role="img" aria-label="calendar">📅</span>
                                <a
                                    href="https://outlook.office365.com/book/eAuditorAuditsInspections@lyonsinfo.com/?ismsaljsauthenabled=true"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ color: '#1e3fd4', textDecoration: 'underline' }}
                                >
                                    Online Booking Calendar
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </Modal.Content>
        </Modal>
    )
}
